import { useState } from 'react';
import { Mail, Phone, MapPin, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Footer } from '@/sections/Footer';
import { SEO } from '@/components/SEO';
import { submitForm } from '@/lib/forms';

export function Contacto() {
  const [form, setForm] = useState({ nombre: '', email: '', telefono: '', asunto: '', mensaje: '' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    try {
      await submitForm('contacto', form);
      setStatus('success');
    } catch (err) {
      console.error('[Contacto] form submission failed:', err);
      setStatus('error');
    }
  };

  return (
    <main className="bg-bg-light">
      <SEO
        title="Contacto | Mi Envío Cuba — Software para Agencias de Envíos"
        description="¿Tienes una agencia de envíos a Cuba y quieres saber más sobre MEC? Contáctanos por email, WhatsApp o formulario. Te respondemos rápido."
        path="/contacto"
        keywords="contacto Mi Envío Cuba, soporte envíos Cuba, atención cliente envíos Cuba, email Mi Envío Cuba"
      />
      <div className="pt-36 sm:pt-44 pb-24 px-4 sm:px-6 lg:px-8 xl:px-12">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-[-0.03em] text-text-primary mb-6">
              Contáctanos.
            </h1>
            <p className="text-xl sm:text-2xl text-text-secondary font-medium leading-snug max-w-xl mx-auto">
              ¿Tienes alguna pregunta? Estamos aquí para ayudarte.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Contact Info */}
            <div className="space-y-6">
              <div className="bg-white rounded-[24px] shadow-card p-6 flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-brand/10 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-brand" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-text-primary mb-1">Email</h3>
                  <a href="mailto:support@mienviocuba.com" className="text-sm text-text-secondary hover:text-brand transition-colors">
                    support@mienviocuba.com
                  </a>
                </div>
              </div>
              <div className="bg-white rounded-[24px] shadow-card p-6 flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-brand/10 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-brand" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-text-primary mb-1">Teléfono</h3>
                  <a href="tel:+17865191709" className="text-sm text-text-secondary hover:text-brand transition-colors">
                    +1 (786) 519-1709
                  </a>
                </div>
              </div>
              <div className="bg-white rounded-[24px] shadow-card p-6 flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-brand/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-brand" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-text-primary mb-1">Ubicación</h3>
                  <a
                    href="https://maps.google.com/?q=25+SW+9th+St+Suite+509+Miami+FL+33130"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-text-secondary hover:text-brand transition-colors"
                  >
                    25 SW 9th St, Suite 509
                    <br />
                    Miami, FL 33130, EE. UU.
                  </a>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-2 bg-white rounded-[28px] shadow-card p-8">
              {status === 'success' ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-14 h-14 text-green-500 mx-auto mb-4" />
                  <h3 className="font-heading text-xl font-semibold text-text-primary mb-2">¡Mensaje enviado!</h3>
                  <p className="text-text-secondary">Te responderemos a la brevedad posible.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-1">Nombre *</label>
                      <input name="nombre" required value={form.nombre} onChange={handleChange}
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
                        placeholder="Tu nombre" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-1">Correo electrónico *</label>
                      <input name="email" type="email" required value={form.email} onChange={handleChange}
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
                        placeholder="tu@email.com" />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-1">Teléfono</label>
                      <input name="telefono" type="tel" value={form.telefono} onChange={handleChange}
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
                        placeholder="+1 (555) 000-0000" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-1">Asunto</label>
                      <select name="asunto" value={form.asunto} onChange={handleChange}
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand bg-white">
                        <option value="">Seleccionar...</option>
                        <option value="Información general">Información general</option>
                        <option value="Soporte técnico">Soporte técnico</option>
                        <option value="Agencias">Agencias</option>
                        <option value="Transitarias">Transitarias</option>
                        <option value="Socios">Socios</option>
                        <option value="Otro">Otro</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-1">Mensaje *</label>
                    <textarea name="mensaje" required value={form.mensaje} onChange={handleChange} rows={5}
                      className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand resize-none"
                      placeholder="¿En qué podemos ayudarte?" />
                  </div>
                  {status === 'error' && (
                    <p className="text-red-500 text-sm text-center">Hubo un error. Por favor intenta de nuevo.</p>
                  )}
                  <Button type="submit" disabled={status === 'loading'}
                    className="w-full rounded-full bg-brand text-white hover:opacity-90 transition-opacity">
                    {status === 'loading' ? 'Enviando...' : 'Enviar mensaje'}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </main>
  );
}
